/**
 * OZDAFNE Ketone Monitoring Guidelines
 *
 * When to check blood ketones and how to respond.
 * Based on OZDAFNE poster "Check Blood Ketones".
 *
 * Last reviewed: February 2026
 */

import { KETONE_THRESHOLDS, BG_THRESHOLDS } from "./thresholds";

// =============================================================================
// KETONE CHECK TRIGGERS
// =============================================================================

export const KETONE_CHECK_TRIGGERS = {
  /** Check ketones when GL is at or above this level, even if feeling well */
  HIGH_GL_THRESHOLD: BG_THRESHOLDS.KETONE_CHECK_TRIGGER, // 15.0 mmol/L

  /** Always check ketones if experiencing these symptoms */
  SYMPTOMS: [
    {
      id: "unwell",
      description: "Feeling unwell, even if GL is in range",
      icon: "emoji-frown",
    },
    {
      id: "thirst",
      description: "Excess thirst or frequent urination",
      icon: "droplet",
    },
    {
      id: "nausea",
      description: "Nausea, vomiting, or abdominal pain",
      icon: "exclamation-triangle",
    },
    {
      id: "breath",
      description: "Fruity-smelling breath",
      icon: "wind",
    },
    {
      id: "drowsy",
      description: "Feeling drowsy or confused",
      icon: "moon",
    },
  ],
} as const;

// =============================================================================
// KETONE ACTION LEVELS
// Based on OZDAFNE guidelines
// =============================================================================

export interface KetoneActionLevel {
  id: string;
  range: { min: number; max: number };
  level: "normal" | "elevated" | "high" | "danger";
  color: string;
  action: string;
  details: string[];
  recheckMinutes?: number;
  seekHelp?: boolean;
}

export const KETONE_ACTION_LEVELS: KetoneActionLevel[] = [
  {
    id: "normal",
    range: { min: 0, max: KETONE_THRESHOLDS.NORMAL_MAX },
    level: "normal",
    color: "success",
    action: "Normal - no action needed",
    details: [
      "Ketones are in the normal range",
      "Continue monitoring if unwell",
    ],
  },
  {
    id: "elevated",
    range: {
      min: KETONE_THRESHOLDS.NORMAL_MAX,
      max: KETONE_THRESHOLDS.ELEVATED_MAX,
    },
    level: "elevated",
    color: "warning",
    action: "Elevated - monitor and hydrate",
    details: [
      "Drink plenty of water",
      "If GL is high, correct with extra insulin",
      "Continue to check ketones every 2-3 hours",
      "Contact your diabetes team if not improving",
    ],
    recheckMinutes: 180,
  },
  {
    id: "high",
    range: {
      min: KETONE_THRESHOLDS.ELEVATED_MAX,
      max: KETONE_THRESHOLDS.HIGH_MAX,
    },
    level: "high",
    color: "danger",
    action: "High - DKA risk, take action now",
    details: [
      "Give extra correction insulin (10-20% more than usual)",
      "Drink plenty of water",
      "Do NOT exercise",
      "Check ketones every 1-2 hours",
      "Seek medical advice if not improving",
    ],
    recheckMinutes: 120,
    seekHelp: true,
  },
  {
    id: "danger",
    range: { min: KETONE_THRESHOLDS.DANGER, max: Infinity },
    level: "danger",
    color: "danger",
    action: "DANGER - Seek medical help immediately",
    details: [
      "This is a medical emergency",
      "Go to hospital emergency department",
      "Do NOT drive yourself",
      "Give correction insulin if able",
      "Keep drinking water",
    ],
    recheckMinutes: 60,
    seekHelp: true,
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the appropriate action level for a given ketone reading.
 */
export function getKetoneActionLevel(ketones: number): KetoneActionLevel {
  for (const level of KETONE_ACTION_LEVELS) {
    if (ketones >= level.range.min && ketones < level.range.max) {
      return level;
    }
  }
  // Default to danger if very high
  return KETONE_ACTION_LEVELS[KETONE_ACTION_LEVELS.length - 1];
}

/**
 * Determine if ketones should be checked based on BG and symptoms.
 */
export function shouldCheckKetones(
  bg: number | undefined,
  hasSymptoms: boolean = false,
): { shouldCheck: boolean; reason: string } {
  if (hasSymptoms) {
    return {
      shouldCheck: true,
      reason: "Check ketones when feeling unwell",
    };
  }

  if (bg !== undefined && bg >= KETONE_CHECK_TRIGGERS.HIGH_GL_THRESHOLD) {
    return {
      shouldCheck: true,
      reason: `Check ketones when GL is ${KETONE_CHECK_TRIGGERS.HIGH_GL_THRESHOLD} mmol/L or higher`,
    };
  }

  return {
    shouldCheck: false,
    reason: "",
  };
}
